// Resume.js
import React from 'react';
import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
  <div style={{ marginBottom: '2rem' }}>
    <h3 style={{
      fontSize: '0.75rem', fontWeight: 800, letterSpacing: '0.18em',
      textTransform: 'uppercase', color: '#6a89cc', borderBottom: '1px solid rgba(106,137,204,0.3)',
      paddingBottom: '0.4rem', marginBottom: '1.2rem'
    }}>{title}</h3>
    {children}
  </div>
);

const Role = ({ org, title, dates, bullets }) => (
  <div style={{ marginBottom: '1.4rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.25rem', marginBottom: '0.2rem' }}>
      <div>
        <span style={{ fontWeight: 700, color: '#f0f4ff' }}>{org}</span>
        {title && <span style={{ color: '#82ccdd', fontWeight: 500 }}> | {title}</span>}
      </div>
      <span style={{ fontSize: '0.85rem', color: '#7a8ba8', whiteSpace: 'nowrap' }}>{dates}</span>
    </div>
    <ul style={{ margin: '0.5rem 0 0 1.2rem', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.3rem' }}>
      {bullets.map((b, i) => (
        <li key={i} style={{ fontSize: '0.9rem', color: '#b8c5d6', lineHeight: 1.65 }}>{b}</li>
      ))}
    </ul>
  </div>
);

const Resume = () => (
  <div id="resume" style={{
    padding: '5rem 2rem',
    background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 100%)',
    color: '#fff',
    minHeight: '100vh',
  }}>
    <div style={{ maxWidth: 860, margin: '0 auto' }}>

      {/* Back link */}
      <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.4rem', color: '#82ccdd', textDecoration: 'none', fontSize: '0.9rem', fontWeight: 600, marginBottom: '2rem' }}>
        ← Back to portfolio
      </Link>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
        <h2 style={{
          fontSize: 'clamp(2rem, 5vw, 2.8rem)', fontWeight: 800, margin: '0 0 0.5rem',
          background: 'linear-gradient(135deg, #ffffff 0%, #82ccdd 100%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
        }}>Shreya Sharma</h2>
        <p style={{ color: '#7a8ba8', fontSize: '0.9rem', margin: '0 0 1.5rem' }}>
          Software Engineering (B.E. Hon.) · Ontario Tech University
        </p>
        <a
          href="/Shreya's_Resume.pdf"
          download
          style={{
            display: 'inline-block', padding: '0.75rem 2rem',
            background: 'linear-gradient(135deg, #6a89cc 0%, #82ccdd 100%)',
            color: '#fff', fontWeight: 700, fontSize: '0.95rem',
            borderRadius: '2rem', textDecoration: 'none', letterSpacing: '0.03em',
          }}
        >
          Download PDF ↓
        </a>
      </div>

      {/* Resume card */}
      <div style={{
        background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(106,137,204,0.2)',
        borderRadius: 16, padding: 'clamp(1.5rem, 4vw, 2.5rem)',
      }}>

        {/* Education */}
        <Section title="Education">
          <Role
            org="Ontario Tech University"
            title="Software Engineering (B.E. Hon.)"
            dates="June 2023"
            bullets={[]}
          />
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.25rem' }}>
            <span style={{ fontSize: '0.9rem', color: '#b8c5d6' }}>AZ-900: Microsoft Azure Fundamentals Certified</span>
            <span style={{ fontSize: '0.85rem', color: '#7a8ba8' }}>July 2022</span>
          </div>
        </Section>

        {/* Work Experience */}
        <Section title="Work Experience">
          <Role
            org="Scotiabank"
            title="Programmer advisory analyst"
            dates="May 2023 – Present"
            bullets={[
              'Designed and developed an internal micro-application integrating LaunchDarkly APIs to expose real-time feature flag status for teams without direct access, improving developer visibility and reducing cross-team dependency.',
              'Led performance optimization initiatives across multiple React micro-apps, improving load time and runtime performance by 12% through bundle analysis, code splitting, memoization, and removal of redundant renders. Architected reusable component patterns and enforced frontend best practices adopted across teams to standardize development of micro-apps.',
              'Mentored junior developers on testing strategies, accessibility standards, and performance optimization techniques. Collaborated with backend, analytics, and platform teams to define API contracts, improve telemetry tracking, and ensure scalable multi-tier architecture.',
              'Drove adoption of Playwright E2E testing and React Testing Library, increasing test coverage and preventing regression issues in production.',
            ]}
          />
          <Role
            org="Scotiabank"
            title="Developer"
            dates="May 2023 – Present"
            bullets={[
              'Built and optimized reusable React components with filtering, loading states, and performance enhancements to improve user experience and application consistency.',
              'Improved performance across applications by applying React optimization techniques such as code splitting, lazy loading, and reusable component patterns.',
              'Ensured accessibility compliance (WCAG/AODA) while supporting multi-tier architecture standards.',
              'Produced weekly KPI reports for 15+ metrics using Dynatrace and Adobe Analytics to provide actionable business insights.',
              'Automated E2E testing with Playwright and updated 50+ test cases using React Testing Library, increasing reliability and reducing production defects.',
              'Integrated Adobe Analytics and Dynamic Tag Management (DTM) for enhanced feature tracking and user behavior analysis.',
            ]}
          />
          <Role
            org="Athabasca University"
            title="LMS course migrator and quality assurance"
            dates="February 2023 – May 2023"
            bullets={[
              'Migrated 30+ courses from Brightspace to Moodle, ensuring content and HTML elements matched during the migration process.',
              'Edited and formatted content for 30+ courses to maintain consistency and enhance user experience on Brightspace.',
              'Conducted thorough quality assurance checks on migrated courses to verify the accuracy of content, formatting, and HTML elements, resulting in an error-free migration process.',
            ]}
          />
          <Role
            org="Ontario Tech University"
            title="Web developer assistant"
            dates="April 2022 – September 2022"
            bullets={[
              'Developed program website utilizing CMS platform with 18+ pages, achieving efficiency & quality through regular test plans.',
              'Worked closely with the IT team to oversee the management of the CMS for the university, ensuring timely updates and maintenance activities were performed to enhance performance and improve the overall user experience.',
            ]}
          />
        </Section>

        {/* Skills */}
        <Section title="Skills">
          {[
            { label: 'Frontend Development', value: 'React.js, React Native, Redux, JavaScript (ES6+), TypeScript, HTML5, CSS3, SCSS, Responsive Design, Accessibility (AODA/WCAG)' },
            { label: 'Testing & QA', value: 'React Testing Library, Playwright, JEST, Unit & E2E Testing' },
            { label: 'Backend & Tools', value: 'Node.js, GraphQL, Firebase, MySQL, SQL' },
            { label: 'Performance Optimization', value: 'Google Lighthouse, Webpack' },
            { label: 'Analytics & Monitoring', value: 'Adobe Analytics, Dynatrace' },
            { label: 'Collaboration & Workflow', value: 'Git, Bitbucket, Jira, Confluence, Agile/Scrum' },
          ].map(({ label, value }) => (
            <div key={label} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.55rem', flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, color: '#82ccdd', fontSize: '0.9rem', minWidth: 180, flexShrink: 0 }}>{label}:</span>
              <span style={{ fontSize: '0.9rem', color: '#b8c5d6', lineHeight: 1.5 }}>{value}</span>
            </div>
          ))}
        </Section>

      </div>
    </div>
  </div>
);

export default Resume;
